import { planTypes } from "components/FormPlan/Context/planType";
import {
  readingIntervalIsReverseError,
  startDateBiggerThenEndDateError,
} from "components/FormPlan/messages";
import { useSnackbarNotification } from "components/SnackbarNotifications";
import { useQuery } from "hooks";
import React, {
  createContext,
  memo,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { trackDelays } from "store/actions/planv2";
import { api, BiblicalPlan, sendErrorEmail, today } from "utils";
import { useChallenge } from "../useChallenge";
import {
  initialEndReading,
  initialStartReading,
  lastDayInYear,
} from "./contants";
import { READING_ORDERS } from "utils/readingOrders";
import {
  INITIAL_END_READING_BY_READING_ORDER,
  INITIAL_START_READING_BY_READING_ORDER,
} from "components/FormPlan/constants";

export const ChallengePlanContext = createContext();

const ChallengePlanProvider = ({ children }) => {
  const [readersRanking, setReadersRanking] = useState([]);
  const [isLoadingRanking, setIsLoadingRanking] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [rankingIsLoaded, setRankingIsLoaded] = useState(false);
  const {
    challengeHeaders,
    request: challenge,
    currentChallenge,
  } = useChallenge();
  const { openGeneralErrorSnackbar } = useSnackbarNotification();

  const reduxTrackDelays = useSelector((state) => state.planv2.trackDelays);
  const [showException, setShowException] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openMoreOptions, setOpenMoreOptions] = useState(false);
  const [formError, setFormError] = useState("");
  const [startReading, setStartReading] = useState(initialStartReading);
  const [endReading, setEndReading] = useState(initialEndReading);
  const [blockedWeekDays, setBlockedWeekDays] = useState([]);
  const [planType, setPlanType] = useState(planTypes.default);
  const [readingOrder, setReadingOrder] = useState("traditional");
  const [quantityOfDays, setQuantityOfDays] = useState(90);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(lastDayInYear);
  const [quantityOfChapters, setQuantityOfChapters] = useState(3);
  const [plan, setPlan] = useState([]);
  const queryPlanType = useQuery("planType");
  const [isViewingForm, setIsViewingForm] = useState(true);
  const [planIsLoaded, setPlanIsLoaded] = useState(false);
  const [planId, setPlanId] = useState();
  const [activeMonth, setActiveMonth] = useState(0);
  const [planGroupedByMonth, setPlanGroupedByMonth] = useState([]);
  const [foundMonthToShow, setFoundMonthToShow] = useState(false);
  const [lastDayIndex, setLastDayIndex] = useState(0);
  const [planWasReset, setPlanWasReset] = useState(false);
  const [isFetchingPlan, setIsFetchingPlan] = useState(false);
  const [delays, setDelays] = useState(0);

  const bibleArrayClass =
    READING_ORDERS[readingOrder] || READING_ORDERS.traditional;
  const biblePlan = new BiblicalPlan(bibleArrayClass);

  const previousPlan = useRef(plan);

  useEffect(() => {
    const isPlanChanged = previousPlan.current.length !== plan.length;
    if (isPlanChanged) {
      setPlanWasReset(true);

      previousPlan.current = plan;
    }
  }, [plan]);

  useEffect(() => {
    setFormError(null);
  }, [startDate, endDate, openMoreOptions]);

  const previousReadingOrder = useRef(readingOrder);

  useEffect(() => {
    const readingOrderChanged = previousReadingOrder.current !== readingOrder;
    if (readingOrderChanged) {
      setStartReading(INITIAL_START_READING_BY_READING_ORDER[readingOrder]);
      setEndReading(INITIAL_END_READING_BY_READING_ORDER[readingOrder]);
      previousReadingOrder.current = readingOrder;
    }
  }, [readingOrder]);

  const updatePlanOnBackend = useCallback(
    async ({ readingCalendar, then = () => {} } = {}) => {
      try {
        await challenge.put("plan/update", {
          newPlan: {
            readingCalendar,
            shouldTrackDelays: reduxTrackDelays,
            blockedWeekDays,
            quantityOfDays,
            chaptersPerDay: quantityOfChapters,
            planType,
            delays: 0,
            readingOrder,
          },
          planTemplateId: currentChallenge.planTemplate,
        });
      } catch (error) {
        setIsLoading(false);
        setShowException(true);
        console.error(error);
        sendErrorEmail(error, "updating challenge plan");
      } finally {
        setIsLoading(false);
        then();
      }
    },
    [
      challenge,
      currentChallenge,
      reduxTrackDelays,
      blockedWeekDays,
      quantityOfDays,
      planType,
      quantityOfChapters,
      readingOrder,
    ]
  );

  const createPlaByQuantityOfDays = (callback = () => {}) => {
    setIsLoading(true);

    try {
      const plan = biblePlan.buildPlanCalendarByQuantityOfDays(
        startDate,
        quantityOfDays,
        startReading,
        endReading,
        [],
        blockedWeekDays
      );

      setPlan(plan);
      updatePlanOnBackend({ readingCalendar: plan });
    } catch (error) {
      console.error(error);
      sendErrorEmail(
        error,
        "creating challenge plan:createPlaByQuantityOfDays"
      );
      openGeneralErrorSnackbar();
    } finally {
      setIsLoading(false);
      callback();
    }
  };

  const createPlaByQuantityOfChapters = useCallback(
    (callback = () => {}) => {
      setIsLoading(true);

      try {
        const plan = biblePlan.buildPlanCalendarByChaptersPerDay(
          startDate,
          startReading,
          endReading,
          Number(quantityOfChapters),
          [],
          blockedWeekDays
        );

        setPlan(plan);
        updatePlanOnBackend({ readingCalendar: plan });
      } catch (error) {
        console.error(error);
        sendErrorEmail(
          error,
          "creating challenge plan:createPlaByQuantityOfChapters"
        );
        openGeneralErrorSnackbar();
      } finally {
        setIsLoading(false);
        callback();
      }
    },
    [
      blockedWeekDays,
      endReading,
      openGeneralErrorSnackbar,
      quantityOfChapters,
      startDate,
      startReading,
      updatePlanOnBackend,
    ]
  );

  const createNormalPlan = useCallback(
    (callback = () => {}) => {
      setIsLoading(true);

      try {
        const plan = biblePlan.buildPlanCalendar(
          startDate,
          endDate,
          startReading,
          endReading,
          [],
          blockedWeekDays
        );

        setPlan(plan);
        updatePlanOnBackend({ readingCalendar: plan });
      } catch (error) {
        console.error(error);
        sendErrorEmail(error, "creating challenge plan:createNormalPlan");
        openGeneralErrorSnackbar();
      } finally {
        setIsLoading(false);
        callback();
      }
    },
    [
      blockedWeekDays,
      endDate,
      endReading,
      openGeneralErrorSnackbar,
      startDate,
      startReading,
      updatePlanOnBackend,
      readingOrder,
      planType,
      quantityOfDays,
      quantityOfChapters,
      planType,
      trackDelays,
      blockedWeekDays,
      quantityOfDays,
    ]
  );

  const readingIntervalIsReverse = useCallback(() => {
    const startBook = Number(startReading.bookIndex);
    const finishBook = Number(endReading.bookIndex);
    const startChapter = Number(startReading.chapter);
    const finishChapter = Number(endReading.chapter);

    return (
      (startBook === finishBook && startChapter > finishChapter) ||
      startBook > finishBook
    );
  }, [endReading, startReading]);

  const startDateIsBiggerThenEndDate = useCallback(
    () => endDate.isBefore(startDate, "day"),
    [endDate, startDate]
  );

  const allInputsAreValid = useCallback(() => {
    if (startDateIsBiggerThenEndDate())
      return setFormError(startDateBiggerThenEndDateError);

    if (readingIntervalIsReverse()) {
      return setFormError(readingIntervalIsReverseError);
    }

    return true;
  }, [readingIntervalIsReverse, startDateIsBiggerThenEndDate]);

  const createPlan = useCallback(
    (callback = () => {}) => {
      switch (
        Number(new URLSearchParams(window.location.search).get("planType"))
      ) {
        case planTypes.default:
          createNormalPlan(callback);
          break;
        case planTypes.quantityOfDays:
          createPlaByQuantityOfDays(callback);
          break;
        case planTypes.chaptersPerDay:
          createPlaByQuantityOfChapters(callback);
          break;
        default:
          createNormalPlan(callback);
      }
    },
    [
      createNormalPlan,
      createPlaByQuantityOfChapters,
      createPlaByQuantityOfDays,
      quantityOfDays,
    ]
  );

  const loadReadersRanking = useCallback(async () => {
    setIsLoadingRanking(true);
    setRankingIsLoaded(false);
    try {
      await api
        .get("/challenge/plan/ranking", challengeHeaders)
        .then(({ data }) => {
          setReadersRanking(data.ranking);
        });
    } catch (err) {
      console.error(err);
      sendErrorEmail(err, "on getReadersRanking function");
      openGeneralErrorSnackbar();
    } finally {
      setIsLoadingRanking(false);
      setRankingIsLoaded(true);
    }
  }, [challengeHeaders, openGeneralErrorSnackbar]);

  const noReaders = useMemo(
    () => !readersRanking.length && !isLoadingRanking,
    [readersRanking, isLoadingRanking]
  );

  useEffect(() => {
    setPlan([]);
    setPlanType(planTypes.default);
    setReadingOrder("traditional");
    setStartReading(initialStartReading);
    setEndReading(initialEndReading);
    setBlockedWeekDays([]);
    setQuantityOfDays(90);
    setStartDate(today);
    setEndDate(lastDayInYear);
    setQuantityOfChapters(3);
    setPlanIsLoaded(false);
    setPlanId(null);
    setActiveMonth(0);
    setPlanGroupedByMonth([]);
    setFoundMonthToShow(false);
    setLastDayIndex(0);
    setPlanWasReset(false);
    setIsFetchingPlan(false);
    setDelays(0);
  }, [currentChallenge._id]);

  return (
    <ChallengePlanContext.Provider
      value={{
        readersRanking,
        isLoadingRanking,
        loadReadersRanking,
        setQuantityOfDays,
        createPlaByQuantityOfDays,
        allInputsAreValid,
        setShowException,
        setIsLoading,
        setStartReading,
        setEndReading,
        setBlockedWeekDays,
        setPlanType,
        setOpenMoreOptions,
        createPlaByQuantityOfChapters,
        setStartDate,
        setEndDate,
        setQuantityOfChapters,
        createNormalPlan,
        createPlan,
        setPlan,
        quantityOfDays,
        showException,
        isLoading,
        openMoreOptions,
        formError,
        startReading,
        endReading,
        blockedWeekDays,
        planType,
        startDate,
        endDate,
        quantityOfChapters,
        trackDelays: reduxTrackDelays,
        plan,
        isViewingForm,
        setIsViewingForm,
        noReaders,
        rankingIsLoaded,
        isLoadingPlan,
        setIsLoadingPlan,
        planIsLoaded,
        setPlanIsLoaded,
        planId,
        setPlanId,
        activeMonth,
        setActiveMonth,
        planGroupedByMonth,
        setPlanGroupedByMonth,
        foundMonthToShow,
        setFoundMonthToShow,
        lastDayIndex,
        setLastDayIndex,
        planWasReset,
        setPlanWasReset,
        isFetchingPlan,
        setIsFetchingPlan,
        delays,
        setDelays,
        readingOrder,
        setReadingOrder,
      }}
    >
      {children}
    </ChallengePlanContext.Provider>
  );
};

export default memo(ChallengePlanProvider);
